/**
 * Direct alert investigation page.
 *
 * Slack notifications and alert inbox rows land here with the Alertmanager
 * fingerprint. The page resolves the active alert first, then loads host
 * context. The AI summary (RCA) is requested only when the admin clicks,
 * using the org_id returned by the alert API.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, EmptyState, Heading, IconButton, Spinner, Text,
} from '@agion/ui'
import {
  AlertTriangle, ArrowLeft, Brain, Clock, ExternalLink, Hash, Server,
  ShieldAlert, Tag, RefreshCw,
} from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatRelativeTime } from '@/core/infra_utils'
import type { ActiveAlert, MonitoringHost } from '@/core/infra_types'
import { deriveAlertMessage, deriveAlertName } from './alert_helpers'
import {
  MonitoringUnavailableError,
  getAlert,
  getHost,
  retryMonitoringRequest,
} from './infra_monitoring.service'
import { AlertRcaSection } from './AlertRcaSection'
import { SeverityBadge, SeverityRibbon, SectionTitle } from './MonitoringPrimitives'

function hostLabel(host?: MonitoringHost | null, alert?: ActiveAlert | null): string {
  return host?.name ?? alert?.hostId ?? ''
}

function annotationURL(alert: ActiveAlert, key: string): string | undefined {
  const raw = alert.annotations?.[key]
  if (!raw) return undefined
  try {
    const url = new URL(raw)
    return url.protocol === 'https:' ? url.toString() : undefined
  } catch {
    return undefined
  }
}

export function InfraAlertDetailPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const { fingerprint = '' } = useParams<{ fingerprint: string }>()

  const {
    data: alert,
    isLoading: alertLoading,
    isFetching: alertFetching,
    error: alertError,
    refetch: refetchAlert,
  } = useQuery({
    queryKey: ['monitoring', 'alert', fingerprint],
    queryFn: () => getAlert(fingerprint),
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: fingerprint !== '',
  })

  const {
    data: host,
    error: hostError,
    isFetching: hostFetching,
    refetch: refetchHost,
  } = useQuery({
    queryKey: ['monitoring', 'host', alert?.hostId],
    queryFn: () => getHost(alert!.hostId),
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
    enabled: !!alert?.hostId,
  })

  const alertName = alert ? deriveAlertName(alert) : t('pages:monitoring.alertDetail.title', 'Alert')
  const alertMessage = alert ? deriveAlertMessage(alert) : ''
  const runbookURL = alert ? annotationURL(alert, 'runbook_url') : undefined
  const generatorURL = alert ? annotationURL(alert, 'generator_url') : undefined
  const sortedLabels = useMemo(() => Object.entries(alert?.labels ?? {}).sort(([a], [b]) => a.localeCompare(b)), [alert])
  const sortedAnnotations = useMemo(() => Object.entries(alert?.annotations ?? {}).sort(([a], [b]) => a.localeCompare(b)), [alert])
  const severityLabel = alert ? t(`pages:monitoring.alerts.severity.${alert.severity}`, alert.severity) : ''
  const statusLabel = alert ? t(`pages:monitoring.alertDetail.status.${alert.status}`, alert.status) : ''
  const detailRefreshing = alertFetching || hostFetching

  async function refreshDetails() {
    if (!alert) {
      await refetchAlert()
      return
    }
    await Promise.allSettled([
      refetchAlert(),
      alert.hostId ? refetchHost() : Promise.resolve(),
    ])
  }

  const fallbackActions = (
    <div className="flex items-center gap-2">
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={t('pages:monitoring.alertDetail.back', 'Back')}
        onClick={() => navigate('/alerts')}
        icon={<ArrowLeft size={16} />}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void refreshDetails()}
        disabled={detailRefreshing}
        className="flex items-center gap-2"
      >
        <RefreshCw size={14} className={detailRefreshing ? 'animate-spin' : ''} />
        {t('pages:monitoring.alerts.refresh', 'Refresh')}
      </Button>
    </div>
  )

  if (alertLoading) {
    return (
      <div className="container-standard">
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      </div>
    )
  }

  if (!alert && alertError instanceof MonitoringUnavailableError) {
    return (
      <div className="container-standard">
        <PageHeader
          icon={AlertTriangle}
          iconColor="text-slate-500"
          title={t('pages:monitoring.alertDetail.title', 'Alert')}
          description={fingerprint}
          actions={fallbackActions}
        />
        <Alert variant="info">
          {t('pages:monitoring.alertDetail.backendPending', 'Active alert details cannot be confirmed until monitoring backend is available.')}
        </Alert>
      </div>
    )
  }

  if (!alert && alertError) {
    return (
      <div className="container-standard">
        <PageHeader
          icon={AlertTriangle}
          iconColor="text-rose-600"
          title={t('pages:monitoring.alertDetail.title', 'Alert')}
          description={fingerprint}
          actions={fallbackActions}
        />
        <Alert variant="error">
          {alertError instanceof Error ? alertError.message : t('pages:monitoring.alertDetail.error', 'Failed to load alert')}
        </Alert>
      </div>
    )
  }

  if (!alert) {
    return (
      <div className="container-standard">
        <PageHeader
          icon={AlertTriangle}
          iconColor="text-slate-500"
          title={t('pages:monitoring.alertDetail.notFoundTitle', 'Alert not active')}
          description={fingerprint}
          actions={fallbackActions}
        />
        <EmptyState
          icon={ShieldAlert}
          title={t('pages:monitoring.alertDetail.notFoundTitle', 'Alert not active')}
          description={t('pages:monitoring.alertDetail.notFound', 'The alert is no longer active or is not visible to this account.')}
          action={
            <Link to="/alerts">
              <Button variant="secondary">
                {t('pages:monitoring.alertDetail.backToAlerts', 'Back to Alerts')}
              </Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="container-standard">
      <PageHeader
        icon={AlertTriangle}
        iconColor={alert.severity === 'critical' ? 'text-rose-600' : alert.severity === 'warning' ? 'text-amber-600' : 'text-sky-600'}
        title={alertName}
        description={alertMessage || fingerprint}
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t('pages:monitoring.alertDetail.back', 'Back')}
              onClick={() => navigate('/alerts')}
              icon={<ArrowLeft size={16} />}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void refreshDetails()}
              disabled={detailRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw size={14} className={detailRefreshing ? 'animate-spin' : ''} />
              {t('pages:monitoring.alerts.refresh', 'Refresh')}
            </Button>
          </div>
        }
      />

      {Boolean(alertError) && (
        <div className="mb-4">
          <Alert variant={alertError instanceof MonitoringUnavailableError ? 'info' : 'error'}>
            {alertError instanceof MonitoringUnavailableError
              ? t('pages:monitoring.alertDetail.staleBackendPending', 'Showing the last loaded alert. Monitoring backend is temporarily unavailable.')
              : t('pages:monitoring.alertDetail.staleRefreshError', 'Showing the last loaded alert. Refresh failed; try again or check backend health.')}
          </Alert>
        </div>
      )}

      <SeverityRibbon severity={alert.severity}>
        <div className="grid gap-6 p-5 md:grid-cols-[minmax(0,1fr),auto]">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <SeverityBadge
                severity={alert.severity}
                label={severityLabel}
                ariaLabel={t('pages:monitoring.alerts.severityAlert', '{{severity}} alert', { severity: severityLabel })}
                icon={AlertTriangle}
              />
              <Badge variant="default">{statusLabel}</Badge>
            </div>
            <Heading level="h2" className="mt-3 break-words text-xl">
              {alertName}
            </Heading>
            {alertMessage && (
              <Text color="muted" className="mt-1 break-words">
                {alertMessage}
              </Text>
            )}
          </div>
          <div className="flex flex-wrap items-start gap-2 md:justify-end">
            {alert.hostId && (
              <>
                <Link to={`/hosts/${alert.hostId}`}>
                  <Button variant="secondary" size="sm" className="flex items-center gap-2">
                    <Server size={14} />
                    {t('pages:monitoring.alertDetail.openHost', 'Open Host')}
                  </Button>
                </Link>
                <Link to={`/agent-hosts/${alert.hostId}/logs`}>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <ExternalLink size={14} />
                    {t('pages:monitoring.hostDetail.openLogs', 'Open Log Viewer')}
                  </Button>
                </Link>
              </>
            )}
            {runbookURL && (
              <a href={runbookURL} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <ExternalLink size={14} />
                  {t('pages:monitoring.alertDetail.runbook', 'Runbook')}
                </Button>
              </a>
            )}
          </div>
        </div>
      </SeverityRibbon>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr),380px]">
        <section className="space-y-6">
          <Card>
            <div className="p-5">
              <SectionTitle>
                <span className="inline-flex items-center gap-2">
                  <Brain size={16} aria-hidden="true" />
                  {t('pages:monitoring.alertDetail.rcaTitle', 'Root Cause Analysis')}
                </span>
              </SectionTitle>
              <AlertRcaSection fingerprint={alert.fingerprint} orgId={alert.orgId} />
            </div>
          </Card>

          <Card>
            <div className="p-5">
              <SectionTitle>
                {t('pages:monitoring.alertDetail.contextTitle', 'Alert Context')}
              </SectionTitle>
              <KeyValueGrid entries={sortedLabels} emptyLabel={t('pages:monitoring.alertDetail.noLabels', 'No labels')} />
            </div>
          </Card>

          <Card>
            <div className="p-5">
              <SectionTitle>
                {t('pages:monitoring.alertDetail.annotationsTitle', 'Annotations')}
              </SectionTitle>
              <KeyValueGrid entries={sortedAnnotations} emptyLabel={t('pages:monitoring.alertDetail.noAnnotations', 'No annotations')} />
            </div>
          </Card>
        </section>

        <aside className="space-y-4">
          <Card>
            <div className="space-y-4 p-5">
              <InvestigationField
                icon={Server}
                label={t('pages:monitoring.alertDetail.host', 'Host')}
                value={hostLabel(host, alert) || t('pages:monitoring.alertDetail.noHostContext', 'No host context')}
                detail={
                  hostError instanceof MonitoringUnavailableError
                    ? t('pages:monitoring.alertDetail.hostContextPending', 'Host context unavailable.')
                    : hostError
                      ? t('pages:monitoring.alertDetail.hostContextError', 'Host context failed to load')
                      : host?.environmentName
                }
              />
              <InvestigationField
                icon={Hash}
                label={t('pages:monitoring.alertDetail.fingerprint', 'Fingerprint')}
                value={alert.fingerprint}
                mono
              />
              <InvestigationField
                icon={Clock}
                label={t('pages:monitoring.alertDetail.started', 'Started')}
                value={formatRelativeTime(alert.startsAt)}
                detail={new Date(alert.startsAt).toLocaleString()}
              />
              <InvestigationField
                icon={RefreshCw}
                label={t('pages:monitoring.alertDetail.updated', 'Updated')}
                value={formatRelativeTime(alert.updatedAt)}
                detail={new Date(alert.updatedAt).toLocaleString()}
              />
              {generatorURL && (
                <a href={generatorURL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200">
                  <ExternalLink size={14} />
                  {t('pages:monitoring.alertDetail.generator', 'Generator')}
                </a>
              )}
            </div>
          </Card>
        </aside>
      </div>
    </div>
  )
}

function InvestigationField({
  icon: Icon,
  label,
  value,
  detail,
  mono,
}: {
  icon: typeof Server
  label: string
  value: string
  detail?: string
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
        <Text size="xs" color="muted" className="uppercase tracking-wider">
          {label}
        </Text>
      </div>
      <div className={mono ? 'break-all font-mono text-xs text-slate-900 dark:text-slate-100' : 'break-words text-sm font-medium text-slate-900 dark:text-slate-100'}>
        {value}
      </div>
      {detail && <Text size="xs" color="subtle" className="mt-1 break-words">{detail}</Text>}
    </div>
  )
}

function KeyValueGrid({ entries, emptyLabel }: { entries: Array<[string, string]>; emptyLabel: string }) {
  if (entries.length === 0) {
    return <Text size="sm" color="muted">{emptyLabel}</Text>
  }

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0 rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
          <dt className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <Tag size={12} aria-hidden="true" />
            <span className="truncate">{key}</span>
          </dt>
          <dd className="break-words text-sm text-slate-900 dark:text-slate-100">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
